import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Breadcrumbs, EmptyState, PageSkeleton } from "@/components/ui";
import { BulkEditFlow } from "@/components/bulk-edits/flow/bulk-edit-flow";
import {
  parseBulkEditFlowSearch,
  type BulkEditFlowSearch,
} from "@/components/bulk-edits/flow/flow-search";
import { usePermissions } from "@/hooks/use-permissions";

export const Route = createFileRoute("/_authenticated/bulk-edits/new")({
  validateSearch: (search: Record<string, unknown>): BulkEditFlowSearch =>
    parseBulkEditFlowSearch(search),
  component: NewBulkEditRoute,
});

/**
 * `/bulk-edits/new`, the new bulk edit flow, gated on `bulk_edits:run`, which
 * includes the `bulk_edits:read` the review step's reads of the draft and its
 * items need. The step, the selection and at review the draft ride the search;
 * every write the flow asks for is a navigation to this route.
 */
function NewBulkEditRoute() {
  const { t } = useTranslation("bulkEdits");
  const navigate = useNavigate();
  const search = Route.useSearch();
  const { can, isLoading } = usePermissions();

  if (isLoading) {
    return (
      <div className="p-4 sm:p-8">
        <PageSkeleton />
      </div>
    );
  }
  if (!can("bulk_edits", "run")) {
    return (
      <div className="p-4 sm:p-8">
        <EmptyState
          title={t("denied.run.title")}
          description={t("denied.run.body")}
          variant="inline"
        />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 sm:p-8">
      <Breadcrumbs
        items={[{ label: t("home.title"), to: "/bulk-edits" }, { label: t("flow.title") }]}
      />
      <BulkEditFlow
        search={search}
        onSearchChange={(next, { replace }) =>
          void navigate({ to: "/bulk-edits/new", search: next, replace })
        }
        onCancel={() => void navigate({ to: "/bulk-edits" })}
        onOpenRun={(runId, options) =>
          void navigate({
            to: "/bulk-edits/$runId",
            params: { runId },
            replace: options?.replace ?? false,
          })
        }
      />
    </div>
  );
}
